import * as React from "react";

export type ToastVariant = "info" | "warning" | "critical" | "success";
export type ToastAlignment = "horizontal" | "vertical";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastLink {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface ToastData {
  id: string;
  variant: ToastVariant;
  title?: string;
  description: string;
  icon?: React.ReactNode;
  hideIcon?: boolean;
  closable?: boolean;
  duration?: number;
  alignment?: ToastAlignment;
  action?: ToastAction;
  link?: ToastLink;
}

export interface ToastOptions {
  title?: string;
  icon?: React.ReactNode;
  hideIcon?: boolean;
  closable?: boolean;
  duration?: number;
  alignment?: ToastAlignment;
  action?: ToastAction;
  link?: ToastLink;
}

interface ToastContextValue {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, "id">) => string;
  removeToast: (id: string) => void;
  removeAllToasts: () => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export const useToastContext = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};

export const ToastContextProvider = ToastContext.Provider;

// Generate unique ID for toasts
let toastCount = 0;
const generateId = () => {
  toastCount += 1;
  return `toast-${toastCount}-${Date.now()}`;
};

// Listener pattern for imperative API
type ToastListener = (toast: Omit<ToastData, "id">) => string;
type DismissListener = (id?: string) => void;

let addToastListener: ToastListener | null = null;
let dismissToastListener: DismissListener | null = null;

export const setToastListeners = (
  addListener: ToastListener,
  dismissListener: DismissListener,
) => {
  addToastListener = addListener;
  dismissToastListener = dismissListener;
};

export const clearToastListeners = () => {
  addToastListener = null;
  dismissToastListener = null;
};

const createToast = (
  variant: ToastVariant,
  description: string,
  options?: ToastOptions,
): string => {
  if (!addToastListener) {
    console.warn(
      "Toast: ToastProvider is not mounted. Toast will not be shown.",
    );
    return "";
  }

  return addToastListener({
    variant,
    description,
    title: options?.title,
    icon: options?.icon,
    hideIcon: options?.hideIcon,
    closable: options?.closable ?? true,
    duration: options?.duration,
    alignment: options?.alignment ?? "horizontal",
    action: options?.action,
    link: options?.link,
  });
};

/**
 * Imperative toast API - can be used outside of React components
 *
 * @example
 * ```tsx
 * import { toast } from '@pfizer/shared/components';
 *
 * // Show an error toast
 * toast.error('Something went wrong');
 *
 * // Show a success toast with options
 * toast.success('File uploaded successfully', {
 *   title: 'Upload Complete',
 *   action: { label: 'View', onClick: () => navigate('/files') }
 * });
 *
 * // Dismiss a specific toast
 * const id = toast.info('Loading...');
 * toast.dismiss(id);
 * ```
 */
export const toast = {
  info: (description: string, options?: ToastOptions) =>
    createToast("info", description, options),

  success: (description: string, options?: ToastOptions) =>
    createToast("success", description, options),

  warning: (description: string, options?: ToastOptions) =>
    createToast("warning", description, options),

  critical: (description: string, options?: ToastOptions) =>
    createToast("critical", description, options),

  /** Alias for critical - for Sonner API compatibility */
  error: (description: string, options?: ToastOptions) =>
    createToast("critical", description, options),

  dismiss: (id?: string) => {
    if (dismissToastListener) {
      dismissToastListener(id);
    }
  },

  dismissAll: () => {
    if (dismissToastListener) {
      dismissToastListener();
    }
  },
};

/**
 * Hook for managing toasts - provides the same API as the imperative toast object
 * but also gives access to the current toast state
 *
 * @example
 * ```tsx
 * const { toasts, addToast, removeToast } = useToast();
 * ```
 */
export const useToast = () => {
  const context = React.useContext(ToastContext);

  return {
    toasts: context?.toasts ?? [],
    toast: {
      info: (description: string, options?: ToastOptions) =>
        context?.addToast({ variant: "info", description, ...options }) ?? "",
      success: (description: string, options?: ToastOptions) =>
        context?.addToast({ variant: "success", description, ...options }) ??
        "",
      warning: (description: string, options?: ToastOptions) =>
        context?.addToast({ variant: "warning", description, ...options }) ??
        "",
      critical: (description: string, options?: ToastOptions) =>
        context?.addToast({ variant: "critical", description, ...options }) ??
        "",
      error: (description: string, options?: ToastOptions) =>
        context?.addToast({ variant: "critical", description, ...options }) ??
        "",
      dismiss: (id?: string) => {
        if (id) {
          context?.removeToast(id);
        } else {
          context?.removeAllToasts();
        }
      },
      dismissAll: () => context?.removeAllToasts(),
    },
    addToast: context?.addToast ?? (() => ""),
    removeToast: context?.removeToast ?? (() => {}),
    removeAllToasts: context?.removeAllToasts ?? (() => {}),
  };
};

export { generateId, ToastContext };
